// Date and time utilities with consistent EST timezone display

// Convert a date to EST timezone and format consistently
export function formatDateWithTimezone(
  date: Date | string,
  options: {
    includeTime?: boolean;
    includeSeconds?: boolean;
    dateOnly?: boolean;
    timeOnly?: boolean;
  } = {}
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (options.dateOnly) {
    return dateObj.toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }) + ' EST';
  }

  if (options.timeOnly) {
    return dateObj.toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: '2-digit',
      minute: '2-digit',
      ...(options.includeSeconds && { second: '2-digit' }),
    }) + ' EST';
  }

  if (options.includeTime) {
    return dateObj.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      ...(options.includeSeconds && { second: '2-digit' }),
    }) + ' EST';
  }

  // Default: date only with EST
  return dateObj.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }) + ' EST';
}

// Format for compact display (e.g., in lists)
export function formatDateCompact(date: Date | string): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return dateObj.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }) + ' EST';
}

// Format time range with EST
export function formatTimeRange(startDate: Date | string, endDate: Date | string): string {
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const startTime = startObj.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
  });

  const endTime = endObj.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${startTime} - ${endTime} EST`;
}

// Maximally condensed session label for dropdowns: "Tue, Jun 16 · 7:30–8:30 PM EDT".
// Keeps weekday, date, start/end time, and timezone; drops the year unless the
// session is in a different year, and merges AM/PM when start and end share it.
export function formatSessionCompact(
  startDate: Date | string,
  endDate: Date | string,
  timezone = 'America/New_York'
): string {
  const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const end = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const sessionYear = start.toLocaleDateString('en-US', { timeZone: timezone, year: 'numeric' });
  const currentYear = new Date().toLocaleDateString('en-US', {
    timeZone: timezone,
    year: 'numeric',
  });

  const datePart =
    start.toLocaleDateString('en-US', {
      timeZone: timezone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    }) + (sessionYear !== currentYear ? `, ${sessionYear}` : '');

  const timeOpts: Intl.DateTimeFormatOptions = {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
  };
  const startTime = start.toLocaleTimeString('en-US', timeOpts); // "7:30 PM"
  const endTime = end.toLocaleTimeString('en-US', timeOpts);
  const [startClock, startMeridiem] = startTime.split(' ');
  const [, endMeridiem] = endTime.split(' ');
  const timePart =
    startMeridiem === endMeridiem ? `${startClock}–${endTime}` : `${startTime}–${endTime}`;

  let abbr = '';
  try {
    abbr =
      new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
        .formatToParts(start)
        .find(p => p.type === 'timeZoneName')?.value ?? '';
  } catch {
    abbr = '';
  }

  return `${datePart} · ${timePart}${abbr ? ` ${abbr}` : ''}`;
}

// Format full date and time range
export function formatDateTimeRange(startDate: Date | string, endDate: Date | string): string {
  const startObj = typeof startDate === 'string' ? new Date(startDate) : startDate;
  const endObj = typeof endDate === 'string' ? new Date(endDate) : endDate;

  const dateStr = startObj.toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const timeRange = formatTimeRange(startObj, endObj);

  return `${dateStr} at ${timeRange}`;
}